import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertCircle } from "lucide-react";

interface DuplicateLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingLog: {
    id: number;
    wellName?: string;
    logDate: Date;
    status: "draft" | "submitted" | "locked";
    operatorInitial?: string;
    gallonsPumped?: string;
  } | null;
  onOpenExisting: () => void;
  onCancel: () => void;
}

export function DuplicateLogModal({
  open,
  onOpenChange,
  existingLog,
  onOpenExisting,
  onCancel,
}: DuplicateLogModalProps) {
  if (!existingLog) return null;

  const statusColors = {
    draft: "bg-yellow-500/10 text-yellow-700 border-yellow-500/20",
    submitted: "bg-blue-500/10 text-blue-700 border-blue-500/20",
    locked: "bg-gray-500/10 text-gray-700 border-gray-500/20",
  };

  const canEdit = existingLog.status === "draft";
  const requiresUnlock = existingLog.status === "submitted" || existingLog.status === "locked";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <DialogTitle>Log Already Exists</DialogTitle>
          </div>
          <DialogDescription>
            A log for this well and date already exists. You can open the existing log instead of creating a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">Date</div>
              <div className="text-sm">
                {new Date(existingLog.logDate).toLocaleDateString()}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Status</div>
              <Badge className={statusColors[existingLog.status]}>
                {existingLog.status}
              </Badge>
            </div>
          </div>

          {existingLog.operatorInitial && (
            <div>
              <div className="text-sm font-medium text-muted-foreground">Operator</div>
              <div className="text-sm">{existingLog.operatorInitial}</div>
            </div>
          )}

          {existingLog.gallonsPumped && (
            <div>
              <div className="text-sm font-medium text-muted-foreground">Gallons Pumped</div>
              <div className="text-sm">{parseFloat(existingLog.gallonsPumped).toLocaleString()} gal</div>
            </div>
          )}

          {requiresUnlock && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 p-3 border border-amber-200 dark:border-amber-900">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Note:</strong> This log is {existingLog.status}. Admin unlock is required to edit.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onOpenExisting}>
            {canEdit ? "Continue and Edit" : "Open Existing Log"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
